import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BookOpen, Plus, Trash2, AlertTriangle } from "lucide-react";

const CATS = ["general", "raw_wood", "hardware", "polish", "assembly", "packing", "safety"];
const empty = { title: "", category: "general", content: "", priority: "normal" };

export default function Guidelines() {
  const { user } = useAuth();
  const canManage = ["admin", "manager"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/guidelines").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/guidelines", form);
      toast.success("Guideline published");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/guidelines/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-8">
      <PageHeader title="Guidelines & Instructions" subtitle="Standard operating procedures and instructions for the team." icon={BookOpen}
        action={canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-guideline-button" className="gap-2 transition-transform duration-200 hover:-translate-y-px"><Plus className="h-4 w-4" /> New Guideline</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Publish Guideline</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Title</Label>
                  <Input data-testid="guideline-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Moisture content standard" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="guideline-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                    </Select></div>
                </div>
                <div className="space-y-2"><Label>Instructions</Label>
                  <Textarea data-testid="guideline-content" rows={5} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Write the standard operating procedure…" /></div>
              </div>
              <DialogFooter><Button data-testid="guideline-save" onClick={create} disabled={!form.title || !form.content}>Publish</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )} />

      {items.length === 0 ? (
        <EmptyState icon={BookOpen} title="No guidelines yet" hint="Publish SOPs and instructions for your team." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {items.map((g) => (
            <Card key={g.id} data-testid={`guideline-card-${g.id}`} className="animate-fade-up rounded-md border-border p-5 shadow-none">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="rounded-full border-primary/30 bg-primary/10 text-[11px] font-semibold capitalize text-primary">{g.category.replace("_", " ")}</Badge>
                  {g.priority === "high" && (
                    <Badge variant="outline" className="gap-1 rounded-full border-destructive/30 bg-destructive/10 text-[11px] font-semibold text-destructive">
                      <AlertTriangle className="h-3 w-3" /> High
                    </Badge>
                  )}
                </div>
                {canManage && <Button variant="ghost" size="icon" data-testid={`guideline-delete-${g.id}`} onClick={() => remove(g.id)}
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>}
              </div>
              <h3 className="mt-3 font-heading text-lg font-bold">{g.title}</h3>
              <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{g.content}</p>
              <p className="mt-4 text-xs text-muted-foreground/70">Posted by {g.created_by}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
